import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Info } from "lucide-react";
import type { FamilyDiseaseHistory } from "@/types/appointments";
import { EmptyState } from "@/components/consultation/EmptyState";

interface FamilyHistoryCardProps {
  familyHistory: FamilyDiseaseHistory[];
  onManageFatherHistory: () => void;
  onManageMotherHistory: () => void;
  onManageSiblingHistory: () => void;
  isCompleted?: boolean;
}

const relationConfig: Record<string, { title: string }> = {
  FATHER: { title: "Father's History" },
  MOTHER: { title: "Mother's History" },
  SIBLING: { title: "Sibling's History" },
};

export const FamilyHistoryCard = ({
  familyHistory,
  onManageFatherHistory,
  onManageMotherHistory,
  onManageSiblingHistory,
  isCompleted = false,
}: FamilyHistoryCardProps) => {
  const getHandler = (relation: string) => {
    if (isCompleted) return undefined;
    switch (relation) {
      case "FATHER":
        return onManageFatherHistory;
      case "MOTHER":
        return onManageMotherHistory;
      case "SIBLING":
        return onManageSiblingHistory;
      default:
        return () => {};
    }
  };

  return (
    <Card className="rounded-3xl border-border/60 bg-card shadow-sm">
      <CardHeader className="p-4 flex flex-row items-center justify-between border-b border-border/60">
        <div className="flex items-center gap-2">
          <CardTitle className="z-h3 text-foreground">Family History</CardTitle>
          <Info className="size-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        {["FATHER", "MOTHER", "SIBLING"].map((relation) => {
          const history = familyHistory.filter((h) => h.relations === relation);
          const config = relationConfig[relation];
          return (
            <div
              key={relation}
              className="p-4 rounded-xl border border-border/60 bg-muted/40 hover:bg-muted/60 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="z-eyebrow text-muted-foreground">
                  {config.title}
                </p>
                {!isCompleted && (
                  <Button
                    size="icon"
                    className="h-9 w-9 rounded-xl"
                    onClick={getHandler(relation)}
                  >
                    <Plus className="size-4" />
                  </Button>
                )}
              </div>
              {history.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {history.map((h) => (
                    <Badge
                      key={h.id}
                      variant="secondary"
                      className="rounded-full bg-muted border border-border/60 text-foreground z-caption font-normal px-3"
                    >
                      {h.disease.display_name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No records found"
                  variant="compact"
                  className="border-none bg-transparent py-2"
                />
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
